import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {ToastNotification} from 'app/modules/Notification/types';
import {ApiLensConvertFeatureFile} from 'app/modules/Remote/Admin';
import api from 'app/modules/Remote/api';
import {HoverProperty} from 'app/modules/Remote/FeatureCollection';
import {ApiProject} from 'app/modules/Remote/Project';
import {ProjectsActions, useProjects} from 'app/providers/ProjectsProvider';

export interface UploadAsOverlayParams {
  featureCollection: geojson.FeatureCollection;
  name: string;
  hoverPropertyKeys?: HoverProperty[];
  allowEmptyName?: boolean;
}

export interface UploadAsOverlayOptions {
  projectId: string;
  selectedProject: I.ImmutableOf<ApiProject>;
  projectsActions: ProjectsActions;
  pushNotification: (n: ToastNotification) => unknown;
  onOverlayAdded?: (overlayName: string) => void;
}

/** Simplified options for the hook — project data is resolved internally. */
export interface UseUploadAsOverlayOptions {
  projectId: string;
  pushNotification: (n: ToastNotification) => unknown;
  onOverlayAdded?: (overlayName: string) => void;
}

/**
 * Pure async function that uploads a GeoJSON FeatureCollection as a project overlay.
 * Suitable for use in class components. The hook wrapper below is preferred in
 * functional components since it also tracks loading state.
 */
export async function uploadAsOverlay(
  {featureCollection, name, hoverPropertyKeys = [], allowEmptyName = false}: UploadAsOverlayParams,
  {
    projectId,
    selectedProject,
    projectsActions,
    pushNotification,
    onOverlayAdded,
  }: UploadAsOverlayOptions
): Promise<void> {
  const blob = new Blob([JSON.stringify(featureCollection)], {type: 'application/json'});
  const file = new File([blob], `${name}.geojson`, {type: 'application/json'});

  // convertOverlay returns the data at the top level (no `data` wrapper)
  const {gcs_shapefile_path} = (
    await api.lens.uploader.convertOverlay(file)
  ).toJS() as ApiLensConvertFeatureFile;

  if (!gcs_shapefile_path) {
    throw new Error('Overlay conversion did not return a file path.');
  }

  // uploadOverlay uses item_response → data lives at response.get('data')
  const newFc = (
    await api.featureCollections.uploadOverlay(
      name,
      gcs_shapefile_path,
      {},
      [],
      hoverPropertyKeys,
      {},
      allowEmptyName
    )
  ).get('data');

  const fcId = newFc.get('id');

  await api.projects.addOverlay(projectId, fcId);

  const currentFcs = selectedProject.get('featureCollections').toJS();
  projectsActions.updateProject(projectId, {
    featureCollections: [...currentFcs, {id: fcId, kind: 'overlay' as const}],
  });

  onOverlayAdded?.(name);

  pushNotification({
    message: `"${name}" added as overlay. Tiles may take a few minutes to process.`,
    options: {intent: B.Intent.SUCCESS},
  });
}

/**
 * Hook wrapper around uploadAsOverlay. Use in functional components.
 * Pass null when overlay uploading is not available in the current context;
 * the returned uploadAsOverlay will be null in that case.
 */
export function useUploadAsOverlay(options: UseUploadAsOverlayOptions | null): {
  uploadAsOverlay: ((params: UploadAsOverlayParams) => Promise<void>) | null;
} {
  const [projects, , projectsActions] = useProjects();
  const selectedProject = options && projects ? (projects.get(options.projectId) ?? null) : null;
  const upload = React.useCallback(
    async (params: UploadAsOverlayParams) => {
      if (!options || !selectedProject) return;
      try {
        await uploadAsOverlay(params, {
          ...options,
          selectedProject,
          projectsActions,
        });
      } catch (e) {
        options.pushNotification({
          message: `Failed to add "${params.name}" as overlay. Please try again.`,
          options: {intent: B.Intent.DANGER},
        });
        console.error('Failed to upload overlay', e);
      }
    },
    [options, selectedProject, projectsActions]
  );
  return {uploadAsOverlay: options && selectedProject ? upload : null};
}
